/**
 * Skill API Service
 *
 * Typed client for all skill-related endpoints:
 * - Skill CRUD
 * - Skill activation/deactivation
 */

import { apiClient } from './api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SkillType = 'knowledge' | 'prompt' | 'toolset' | 'mcp_server';
export type SkillScope = 'org' | 'team' | 'user';
export type SkillStatus = 'active' | 'disabled' | 'draft';

export interface McpConfig {
  server_url: string;
  transport: 'stdio' | 'sse';
  env: Record<string, string>;
  tools_whitelist: string[];
}

export interface Skill {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  type: SkillType;
  content_md: string | null;
  tools_definitions: unknown | null;
  mcp_config: McpConfig | null;
  scope: SkillScope;
  status: SkillStatus;
  version: number;
  is_activated_by_current_user: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateSkillInput {
  name: string;
  slug: string;
  description?: string;
  type: SkillType;
  content_md?: string;
  tools_definitions?: unknown;
  mcp_config?: McpConfig;
  scope: SkillScope;
  owner_team_id?: string;
}

export interface UpdateSkillInput {
  name?: string;
  description?: string;
  content_md?: string;
  tools_definitions?: unknown;
  mcp_config?: McpConfig;
  status?: SkillStatus;
}

export interface ListSkillsParams {
  scope?: SkillScope | 'all';
  type?: SkillType;
  page?: number;
  per_page?: number;
}

export interface ListSkillsResponse {
  data: Skill[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// ---------------------------------------------------------------------------
// API Methods
// ---------------------------------------------------------------------------

const SKILLS_PATH = '/v1/skills';

export const skillApi = {
  /** List skills */
  async list(params?: ListSkillsParams): Promise<ListSkillsResponse> {
    const query = new URLSearchParams();
    if (params?.scope) query.set('scope', params.scope);
    if (params?.type) query.set('type', params.type);
    if (params?.page) query.set('page', String(params.page));
    if (params?.per_page) query.set('per_page', String(params.per_page));
    const qs = query.toString();
    return apiClient.get<ListSkillsResponse>(`${SKILLS_PATH}${qs ? `?${qs}` : ''}`);
  },

  /** Get skill by ID */
  async get(id: string): Promise<Skill> {
    return apiClient.get<Skill>(`${SKILLS_PATH}/${id}`);
  },

  /** Create skill */
  async create(input: CreateSkillInput): Promise<Skill> {
    return apiClient.post<Skill>(SKILLS_PATH, input);
  },

  /** Update skill */
  async update(id: string, input: UpdateSkillInput): Promise<Skill> {
    return apiClient.patch<Skill>(`${SKILLS_PATH}/${id}`, input);
  },

  /** Soft-delete skill */
  async delete(id: string): Promise<void> {
    return apiClient.delete<void>(`${SKILLS_PATH}/${id}`);
  },

  /** Duplicate skill */
  async duplicate(id: string, input?: { new_name?: string; new_slug?: string; scope?: SkillScope }): Promise<Skill> {
    return apiClient.post<Skill>(`${SKILLS_PATH}/${id}/duplicate`, input ?? {});
  },

  /** Activate skill for current user */
  async activate(id: string): Promise<void> {
    return apiClient.post<void>(`${SKILLS_PATH}/${id}/activate`);
  },

  /** Deactivate skill for current user */
  async deactivate(id: string): Promise<void> {
    return apiClient.post<void>(`${SKILLS_PATH}/${id}/deactivate`);
  },
};
